// Library Methods
import { useCallback, useEffect, useRef, useState } from "react";

// Services
import { watchUrl } from "src/services/liveView/api";
import { contentTypeOf, decodeFrame } from "src/services/liveView/protocol";

// Types
import { LiveViewConnectionState } from "src/services/liveView/types";

type UseLiveViewType = {
  clinicId: string | undefined;
  deviceId: string | null;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
};

const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_DELAY_MS = 10000;

const useLiveView = ({ clinicId, deviceId, canvasRef }: UseLiveViewType) => {
  const [connectionState, setConnectionState] =
    useState<LiveViewConnectionState>("idle");
  const [error, setError] = useState<string | null>(null);

  const pendingRef = useRef<ArrayBuffer | null>(null);
  const decodingRef = useRef(false);

  const drawPending = useCallback(async () => {
    if (decodingRef.current) return;

    const buffer = pendingRef.current;
    pendingRef.current = null;
    if (!buffer) return;

    decodingRef.current = true;

    try {
      const frame = decodeFrame(buffer);
      if (!frame) {
        console.error(
          "Live view frame dropped: unrecognized header",
          `bytes=${buffer.byteLength}`,
          Array.from(new Uint8Array(buffer.slice(0, 8)))
        );
        return;
      }

      const canvas = canvasRef.current;
      if (!canvas) return;

      // Latest-frame-wins: exam-service drops frames to slow viewers rather than
      // buffering, so the canvas only ever needs to hold the most recent bitmap.
      const bitmap = await createImageBitmap(
        new Blob([frame.payload], { type: contentTypeOf(frame.header.format) })
      ).catch((e) => {
        console.error(
          "Live view frame dropped: could not decode image payload",
          `format=${frame.header.format}`,
          `bytes=${frame.payload.byteLength}`,
          e
        );
        return null;
      });
      if (!bitmap) return;

      if (canvas.width !== bitmap.width || canvas.height !== bitmap.height) {
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
      }

      canvas.getContext("2d")?.drawImage(bitmap, 0, 0);
      bitmap.close();
    } finally {
      decodingRef.current = false;
      if (pendingRef.current) void drawPending();
    }
  }, [canvasRef]);

  useEffect(() => {
    if (!clinicId || !deviceId) return;

    let cancelled = false;
    let socket: WebSocket | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;

    const connect = () => {
      if (cancelled) return;

      pendingRef.current = null;
      setConnectionState((previous) =>
        previous === "open" ? previous : "connecting"
      );

      const address = watchUrl({ clinicId, deviceId });
      socket = new WebSocket(address);
      socket.binaryType = "arraybuffer";

      socket.onopen = () => {
        attempt = 0;
        setError(null);
        setConnectionState("open");
      };

      socket.onmessage = (event) => {
        // Binary messages are relayed frames; text messages are relay status
        // frames, which this viewer doesn't currently surface.
        if (typeof event.data === "string") return;

        pendingRef.current = event.data as ArrayBuffer;
        void drawPending();
      };

      // The WebSocket spec deliberately withholds detail from `error` events,
      // so the close code/reason (logged in onclose) is what actually
      // explains a drop.
      socket.onerror = (event) => {
        console.error("Live view socket error", address, event);
      };

      socket.onclose = (event) => {
        console.error(
          "Live view socket closed",
          address,
          `code=${event.code}`,
          `reason=${event.reason || "(none)"}`
        );
        if (cancelled) return;

        // Retry instead of giving up -- a VR headset losing Wi-Fi for a
        // moment, or the relay restarting, should recover on its own rather
        // than leaving the viewer stuck on a dead connection. Quick at
        // first, then backing off so a sustained outage doesn't hammer it.
        setError("Reconnecting to the live stream…");
        setConnectionState("closed");
        attempt += 1;
        const delay = Math.min(
          RECONNECT_BASE_DELAY_MS * attempt,
          RECONNECT_MAX_DELAY_MS
        );
        retryTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      socket?.close();
    };
  }, [clinicId, deviceId, drawPending]);

  return { connectionState, error };
};

export default useLiveView;
